<template>
  <div
    v-tooltip.permanent="tooltip"
    :data-row-index="rowIndex"
    :data-cell="isCell"
    :data-edit-cell="editCell || undefined"
    :data-checked="isDataChecked"
    :data-column="columnName"
    :data-copy-value="copyValue"
    class="ui-grid__cell"
    :class="mainClasses"
    @click="handleClick"
    @dblclick="handleDblClick"
    @contextmenu="handleContextMenu"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <RowCellActions
      v-if="isActionsVisible"
      :items="rowActions"
      :item
      @mouseup.stop
      @mousedown.stop
    >
      <slot
        name="actions"
        v-bind="{ item, rowIndex }"
        :size="'sm' as ButtonSize"
      />
    </RowCellActions>
    <RowCellMenu
      v-if="isMenuVisible"
      @click="handleClickMenu"
    />
    <div
      v-if="isCaptionVisible"
      class="ui-grid__caption"
    >
      {{ displayCaption }}:&nbsp;
    </div>
    <div
      ref="valueRef"
      class="ui-grid__cell-value"
      :class="valueClasses"
      @click="handleClickValue"
    >
      <slot
        v-bind="{
          active,
          columnName,
          displayValue,
          editCell,
          editRow,
          item,
          size: 'sm' as InputSize,
          rowIndex,
        }"
      >
        {{ displayValue || (column?.clickableValue ? '' : '&nbsp;') }}
      </slot>
    </div>
  </div>
</template>

<script setup lang="ts" generic="T">
import { ComputedRef, Ref, computed, inject, ref, useSlots, useTemplateRef } from 'vue'
import { useResizeObserver } from '@vueuse/core'
import { get } from 'lodash'

import { ButtonSize, DottedMenuItem, InputSize } from '@types'
import {
  GridCellFilterHandler,
  GridColumn,
  GridItem,
  GridValueGetter,
  GridValuePaster,
  GridValueCopier,
} from '../utils/types'

import RowCellMenu from './RowCellMenu.vue'
import RowCellActions from './RowCellActions.vue'

type Props = {
  column?: GridColumn
  item: T
  rowIndex: number

  active?: boolean
  editRow?: boolean
  editCell?: boolean

  isFirst?: boolean
  isLast?: boolean

  isCollapsed?: boolean

  rowActions?: DottedMenuItem[]
}

type Emits = {
  click: []
  dblclick: []
  'click:menu': []
  'click:value': []
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

defineOptions({
  name: 'GridRowCell',
})

defineExpose({
  paste(data: string | number, check = true) {
    if (!columnName.value) return
    valuePaster?.(columnName.value, props.item, data, check)
  },
})

const slots = useSlots()

const disabled = inject('disabled', false)
const hasRowMenu = inject<ComputedRef<boolean>>('hasRowMenu')
const isHovered = inject<Ref<boolean>>('isHovered')
const isChecked = inject<ComputedRef<boolean>>('isChecked')
const valueGetter = inject<GridValueGetter>('valueGetter')
const valuePaster = inject<GridValuePaster>('valuePaster')
const valueCopier = inject<GridValueCopier>('valueCopier')
const cellFilterHandler = inject<GridCellFilterHandler | undefined>('cellFilterHandler', undefined)

const valueRef = useTemplateRef('valueRef')
const isValueEllipsis = ref(false)

const columnName = computed(() => props.column?.name)
const displayCaption = computed(() => props.column?.caption)

const copyValue = computed(() => {
  return (props.item && columnName.value && valueCopier?.(columnName.value, props.item)) || undefined
})

const value = computed(() => {
  return (
    props.item &&
    columnName.value &&
    (valueGetter?.(columnName.value, props.item) ||
      get(props.item, columnName.value.split('.')))
  )
})

const displayValue = computed(() => {
  if (!props.item || !props.column?.formatter) return value.value
  const { formatter } = props.column
  return formatter(value.value, props.item as GridItem, props.isCollapsed)
})

const isCell = computed(() =>
  disabled || !props.column || props.column.silent || props.editRow
    ? undefined
    : 'true',
)
const isDataChecked = computed(() =>
  !isCell.value || !isChecked?.value ? undefined : 'true',
)

const isEditing = computed(() => props.editRow || props.editCell)

const mainClasses = computed(() => {
  const cellClasses = typeof props.column?.cellClasses === 'string' ?
    props.column.cellClasses :
    props.column?.cellClasses?.(props.item, columnName.value)
  return {
    'ui-grid__cell--first': !props.isCollapsed && props.isFirst,
    'ui-grid__cell--last': !props.isCollapsed && props.isLast,
    'ui-grid__cell--hovered': !props.isCollapsed && !disabled && isHovered?.value,
    'ui-grid__cell--highlighted':
    !props.isCollapsed && props.active && !props.editRow,
    'ui-grid__cell--checkbox': !props.column,
    [`${cellClasses}`]: cellClasses,
  }
})

const lineClamp = computed(() => props.column?.lineClamp)

const valueClasses = computed(() => {
  const valueClasses = typeof props.column?.cellValueClasses === 'string' ?
    props.column.cellValueClasses :
    props.column?.cellValueClasses?.(props.item, columnName.value)
  return {
    'ui-grid__cell-value--truncated':
    props.column && (!lineClamp.value || lineClamp.value === 1) && !isEditing.value,
    'ui-grid__cell-value--edit': props.editCell,
    [`ui-grid__cell-value--line-clamp-${lineClamp.value}`]:
    lineClamp.value && lineClamp.value > 1 && !isEditing.value,
    [`${valueClasses}`]: valueClasses,
    'ui-grid__cell-value--clickable': props.column?.clickableValue,
  }
})

const isMenuVisible = computed(
  () =>
    hasRowMenu?.value &&
    !props.editRow &&
    ((!props.isCollapsed && props.isFirst) ||
      (props.isCollapsed && props.isLast)),
)

const isActionsVisible = computed(() => {
  return (
    slots.actions &&
    !props.editRow &&
    ((!props.isCollapsed && props.isFirst) ||
      (props.isCollapsed && props.isLast))
  )
})

const isCaptionVisible = computed(
  () =>
    props.column && props.isCollapsed && !props.column.hideCaptionIfCollapsed,
)

const tooltip = computed(() => {
  let content
  if (props.column?.tooltip && columnName.value) {
    content = props.column.tooltip(columnName.value, props.item)
  } else if (isValueEllipsis.value) {
    content = displayValue.value
  }
  if (Array.isArray(content)) {
    content = content.join('<br />')
  }
  if (!content) return
  return {
    content,
    handleResize: true,
    html: true,
    placement: tooltipPlacement.value,
  }
})

const tooltipPlacement = computed(
  () => props.column?.tooltipPlacement || 'top',
)

const handleDblClick = () => {
  emit('dblclick')
}

const handleClick = () => {
  emit('click')
}

const handleClickMenu = () => {
  emit('click:menu')
}

const handleContextMenu = (e: MouseEvent) => {
  if (!cellFilterHandler || !isCell.value || props.editCell) return

  const field = columnName.value
  const value = typeof displayValue.value === 'string'
    ? displayValue.value.trim()
    : displayValue.value

  if (!field || value === undefined || value === null || value === '') return

  e.preventDefault()
  cellFilterHandler({ event: e, field, value, item: props.item as GridItem })
}

const handleClickValue = (event: MouseEvent) => {
  if (!props.column?.clickableValue) return
  event.stopPropagation()
  emit('click:value')
}

const handleMouseEnter = () => {
  isHovered && (isHovered.value = true)
}

const handleMouseLeave = () => {
  isHovered && (isHovered.value = false)
}

useResizeObserver(valueRef, () => {
  if (!valueRef.value || isEditing.value || props.column?.disableTooltip) return
  isValueEllipsis.value =
    !props.isCollapsed &&
    (valueRef.value.offsetHeight < valueRef.value.scrollHeight ||
      valueRef.value.offsetWidth < valueRef.value.scrollWidth)
})
</script>

<style>
.ui-grid__cell {
  @apply relative;
  @apply flex items-center;
  @apply relative;
  @apply px-4 py-1;

  &:not(.ui-grid__cell--edit) {
    @apply cursor-pointer;
  }

  &--hovered {
    @apply bg-body-gray;
  }

  &--highlighted {
    @apply bg-primary-light dark:bg-primary-light/40;
  }

  &--checkbox {
    @apply absolute top-[0.575rem] -right-1.5;
    @apply z-10;
  }

  &--checkbox + & {
    @apply pr-10;
  }

  &--span-2 {
    @apply col-span-2;
  }

  &[data-selected]:not([data-edit-cell]) {
    @apply before:content-[''];
    @apply before:absolute before:inset-px;
    @apply before:border;
    @apply before:border-primary-selected dark:before:border-primary-selected/40;

    &[data-copy] {
      @apply before:border-dashed;
    }
  }
}

.ui-grid__cell-value {
  @apply relative;
  @apply flex-auto;
  @apply text-sm sm:text-xs;
  @apply text-gray-500 dark:text-gray-400;

  .ui-grid__cell--edit & {
    @apply flex-none;
  }

  &--truncated {
    @apply truncate;
  }

  &--line-clamp-2 {
    @apply line-clamp-2;
  }

  &--line-clamp-3 {
    @apply line-clamp-3;
  }

  &--line-clamp-4 {
    @apply line-clamp-4;
  }

  &--edit {
    @apply -mx-1.5;
    @apply min-w-27;
  }

  &--clickable {
    @apply underline hover:no-underline;
    @apply underline-offset-2 decoration-gray-400 dark:decoration-gray-500;
    @apply cursor-pointer;
  }
}

.ui-grid:not(.ui-grid--collapsed) {
  .ui-grid__cell {
    @apply min-h-12.5;
    @apply px-2 py-0;
    @apply border-b border-common-border;

    &--first {
      @apply pl-4 sm:pl-default;
    }

    &--last {
      @apply pr-4 sm:pr-default;
    }

    &--checkbox {
      @apply relative top-auto right-auto;
      @apply sticky left-0 z-20;
      @apply bg-grid-cell;

      &.ui-grid__cell--hovered {
        @apply bg-grid-cell-hovered;
      }

      &.ui-grid__cell--highlighted {
        @apply bg-grid-cell-highlighted;
        @apply dark:bg-grid-cell-highlighted/40;
      }
    }

    &--checkbox + & {
      @apply pr-2;
    }

    &--span-2 {
      @apply col-auto;
    }

    &--right {
      @apply text-right;
    }
  }

  .ui-grid__cell-value {
    @apply flex-auto;
  }
}

.ui-grid__caption {
  @apply shrink-0;
  @apply text-sm sm:text-xs;
  @apply text-ellipsis overflow-x-hidden;
  @apply text-gray-400 dark:text-gray-500;
}
</style>
